import React, { useState, useEffect } from 'react';
import { useStore } from '@/store/useStore';
import api from '@/lib/api';
import { 
  TrendingUp,
  TrendingDown,
  Target,
  DollarSign,
  AlertTriangle,
  PieChart,
  BarChart3,
  Layers,
  Info,
  Loader2,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  Cell,
  Legend,
  PieChart as RechartsPie,
  Pie
} from 'recharts';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const Analytics = () => {
  const { operators, priceDeck } = useStore();
  const [selectedOperator, setSelectedOperator] = useState('all');
  const [growthData, setGrowthData] = useState(null);
  const [efficiencyData, setEfficiencyData] = useState(null);
  const [concentrationData, setConcentrationData] = useState(null);
  const [vintageData, setVintageData] = useState(null);
  const [priceImpact, setPriceImpact] = useState(null);
  const [momentumData, setMomentumData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const [growth, efficiency, concentration, vintage, price] = await Promise.all([
          api.get(`/analytics/growth-decomposition?operator_id=${selectedOperator}`),
          api.get(`/analytics/capital-efficiency?operator_id=${selectedOperator}`),
          api.get('/analytics/concentration-risk'),
          api.get(`/analytics/well-vintage?operator_id=${selectedOperator}`),
          api.get(`/analytics/price-impact?price_deck=${priceDeck}`)
        ]);
        
        setGrowthData(growth.data);
        setEfficiencyData(efficiency.data);
        setConcentrationData(concentration.data);
        setVintageData(vintage.data);
        setPriceImpact(price.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedOperator, priceDeck]);

  // Fetch momentum data when operator is selected
  useEffect(() => {
    const fetchMomentum = async () => {
      if (selectedOperator === 'all' || !selectedOperator) {
        setMomentumData(null);
        setForecastData(null);
        return;
      }
      try {
        const [momentum, forecast] = await Promise.all([
          api.get(`/analytics/momentum-methodology?operator_id=${selectedOperator}`),
          api.get(`/analytics/forecast-confidence?operator_id=${selectedOperator}&months=6`)
        ]);
        setMomentumData(momentum.data);
        setForecastData(forecast.data);
      } catch (error) {
        console.error('Error fetching momentum:', error);
      }
    };
    fetchMomentum();
  }, [selectedOperator]);

  const growthChartData = growthData ? [
    { name: 'New Wells', value: growthData.components.new_wells.percentage, fill: '#10B981' },
    { name: 'Base Change', value: Math.abs(growthData.components.base_production_change.percentage), fill: growthData.components.base_production_change.direction === 'positive' ? '#3B82F6' : '#EF4444' },
    { name: 'Decline Impr.', value: growthData.components.decline_slope_improvement.percentage, fill: '#F59E0B' }
  ] : [];

  const vintageChartData = vintageData?.vintage_breakdown?.map(v => ({
    year: v.year,
    production: v.total_production,
    wells: v.well_count,
    share: v.share_of_total
  })) || [];

  const concentrationChartData = concentrationData?.concentration?.top_3_operators?.map((op, i) => ({
    name: op.name.split(' ')[0],
    share: op.share,
    fill: i === 0 ? '#F59E0B' : i === 1 ? '#3B82F6' : '#10B981'
  })) || [];

  return (
    <TooltipProvider>
      <div className="space-y-3 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
              Advanced Analytics
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Institutional-grade intelligence
            </p>
          </div>
          <Select value={selectedOperator} onValueChange={setSelectedOperator}>
            <SelectTrigger className="w-full sm:w-48 lg:w-64 bg-surface border-white/10 h-8 text-xs">
              <SelectValue placeholder="Select operator" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10 max-h-80">
              <SelectItem value="all" className="text-xs">All Operators (Basin)</SelectItem>
              {operators?.map(op => (
                <SelectItem key={op.id} value={op.id} className="text-xs">{op.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Row 1: Growth Decomposition & Capital Efficiency */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
          {/* Growth Source Breakdown */}
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-primary" />
                  Growth Decomposition
                </CardTitle>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-surface border-white/10">
                    <p className="text-xs">Breaks down production changes into: new well contributions, base production changes, and decline curve improvements.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="h-32 sm:h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={growthChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: '#A1A1AA', fontSize: 10 }} width={70} />
                        <RechartsTooltip 
                          contentStyle={{ backgroundColor: '#0F0F10', border: '1px solid rgba(255,255,255,0.1)', fontSize: '11px' }}
                          formatter={(v) => [`${v.toFixed(1)}%`, 'Share']}
                        />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {growthChartData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded bg-success/10 border border-success/20">
                      <p className="text-[10px] text-muted-foreground">New Wells</p>
                      <p className="text-sm font-bold text-success">{growthData?.components.new_wells.well_count}</p>
                    </div>
                    <div className="p-2 rounded bg-secondary/10 border border-secondary/20">
                      <p className="text-[10px] text-muted-foreground">Base Chg</p>
                      <p className={cn("text-sm font-bold", growthData?.components.base_production_change.direction === 'positive' ? 'text-success' : 'text-danger')}>
                        {formatNumber(growthData?.components.base_production_change.value || 0)}
                      </p>
                    </div>
                    <div className="p-2 rounded bg-primary/10 border border-primary/20">
                      <p className="text-[10px] text-muted-foreground">Decline Impr</p>
                      <p className="text-sm font-bold text-primary">{formatNumber(growthData?.components.decline_slope_improvement.value || 0)}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Capital Efficiency */}
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5 text-success" />
                  Capital Efficiency
                </CardTitle>
                <Badge variant="outline" className={cn(
                  "text-[10px] font-mono",
                  (efficiencyData?.efficiency_score || 0) >= 70 ? "border-success/50 text-success" : 
                  (efficiencyData?.efficiency_score || 0) >= 40 ? "border-primary/50 text-primary" : "border-danger/50 text-danger"
                )}>
                  Score: {efficiencyData?.efficiency_score?.toFixed(0) || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded bg-white/[0.02] border border-white/5">
                      <p className="text-[10px] text-muted-foreground uppercase">Prod/Active Well</p>
                      <p className="text-lg sm:text-xl font-bold text-foreground">
                        {formatNumber(efficiencyData?.production_per_active_well || 0)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">BBL/well</p>
                    </div>
                    <div className="p-3 rounded bg-white/[0.02] border border-white/5">
                      <p className="text-[10px] text-muted-foreground uppercase">Output/New Well</p>
                      <p className="text-lg sm:text-xl font-bold text-foreground">
                        {formatNumber(efficiencyData?.estimated_output_per_incremental_well || 0)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">BBL est.</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">3M Trend</span>
                      <span className={cn("font-mono", (efficiencyData?.trends?.trend_3m || 0) >= 0 ? "text-success" : "text-danger")}>
                        {(efficiencyData?.trends?.trend_3m || 0) >= 0 ? '+' : ''}{efficiencyData?.trends?.trend_3m?.toFixed(1)}%
                      </span>
                    </div>
                    <Progress value={50 + (efficiencyData?.trends?.trend_3m || 0)} className="h-1.5" />
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">6M Trend</span>
                      <span className={cn("font-mono", (efficiencyData?.trends?.trend_6m || 0) >= 0 ? "text-success" : "text-danger")}>
                        {(efficiencyData?.trends?.trend_6m || 0) >= 0 ? '+' : ''}{efficiencyData?.trends?.trend_6m?.toFixed(1)}%
                      </span>
                    </div>
                    <Progress value={50 + (efficiencyData?.trends?.trend_6m || 0)} className="h-1.5" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Concentration Risk & Well Vintage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
          {/* Concentration Risk */}
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <PieChart className="w-3.5 h-3.5 text-danger" />
                  Concentration Risk
                </CardTitle>
                <Badge variant="outline" className={cn(
                  "text-[10px] font-mono uppercase",
                  concentrationData?.risk_assessment?.level === 'high' ? "border-danger/50 text-danger" :
                  concentrationData?.risk_assessment?.level === 'medium' ? "border-primary/50 text-primary" :
                  "border-success/50 text-success"
                )}>
                  {concentrationData?.risk_assessment?.level || 'N/A'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={concentrationChartData}
                            dataKey="share"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={20}
                            outerRadius={40}
                          >
                            {concentrationChartData.map((entry, idx) => (
                              <Cell key={idx} fill={entry.fill} />
                            ))}
                          </Pie>
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-muted-foreground">Top 3 Share</span>
                        <span className="font-mono font-bold text-foreground">
                          {concentrationData?.concentration?.top_3_share?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-muted-foreground">Top 1 Share</span>
                        <span className="font-mono text-foreground">
                          {concentrationData?.concentration?.top_1_share?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-muted-foreground">HHI Index</span>
                        <span className="font-mono text-foreground">
                          {concentrationData?.concentration?.hhi?.toFixed(0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {concentrationData?.concentration?.top_3_operators?.map((op, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-white/[0.02]">
                        <span className="text-foreground truncate max-w-[150px]">{op.name}</span>
                        <span className="font-mono text-muted-foreground">{op.share.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Well Vintage */}
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-secondary" />
                  Well Vintage Mix
                </CardTitle>
                {vintageData?.summary?.decline_cliff_risk && (
                  <Badge variant="outline" className="border-danger/50 text-danger text-[10px]">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Cliff Risk
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={vintageChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                        <XAxis dataKey="year" tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} tickFormatter={formatNumber} />
                        <RechartsTooltip 
                          contentStyle={{ backgroundColor: '#0F0F10', border: '1px solid rgba(255,255,255,0.1)', fontSize: '11px' }}
                        />
                        <Bar dataKey="production" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded bg-success/10 border border-success/20 text-center">
                      <p className="text-[10px] text-muted-foreground">Recent Vintage</p>
                      <p className="text-sm font-bold text-success">{vintageData?.summary?.recent_vintage_share?.toFixed(1)}%</p>
                    </div>
                    <div className="p-2 rounded bg-secondary/10 border border-secondary/20 text-center">
                      <p className="text-[10px] text-muted-foreground">Mature Vintage</p>
                      <p className="text-sm font-bold text-secondary">{vintageData?.summary?.mature_vintage_share?.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 3: Price Impact & Forecast Confidence */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
          {/* Price Impact */}
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-success" />
                  Price Impact @ ${priceDeck}
                </CardTitle>
                <Badge variant="outline" className="border-primary/50 text-primary text-[10px] font-mono">
                  {priceImpact?.summary?.total_delta_pct >= 0 ? '+' : ''}{priceImpact?.summary?.total_delta_pct?.toFixed(1)}%
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                      <p className="text-[10px] text-muted-foreground">Revenue Delta</p>
                      <p className={cn("text-lg font-bold", (priceImpact?.summary?.total_delta || 0) >= 0 ? "text-success" : "text-danger")}>
                        ${formatNumber(Math.abs(priceImpact?.summary?.total_delta || 0))}
                      </p>
                    </div>
                    <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                      <p className="text-[10px] text-muted-foreground">At Risk</p>
                      <p className="text-lg font-bold text-danger">
                        {priceImpact?.summary?.operators_at_risk || 0}
                        <span className="text-xs text-muted-foreground ml-1">operators</span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {priceImpact?.operators?.slice(0, 6).map((op, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1.5 rounded bg-white/[0.02]">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground w-4">{op.rank}</span>
                          <span className="text-foreground truncate max-w-[100px]">{op.operator_name.split(' ')[0]}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn(
                            "text-[10px]",
                            op.at_risk ? "border-danger/50 text-danger" : "border-success/50 text-success"
                          )}>
                            {op.viability_score.toFixed(0)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Forecast Confidence Band */}
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-primary" />
                  Forecast Confidence
                </CardTitle>
                {forecastData && (
                  <Badge variant="outline" className={cn(
                    "text-[10px] font-mono",
                    (forecastData?.confidence_metrics?.slope_delta || 0) >= 0 ? "border-success/50 text-success" : "border-danger/50 text-danger"
                  )}>
                    Δ {forecastData?.confidence_metrics?.slope_delta_percent?.toFixed(0)}%
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              {selectedOperator === 'all' ? (
                <div className="h-48 flex items-center justify-center text-xs text-muted-foreground">
                  Select an operator to view forecast
                </div>
              ) : !forecastData ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="h-36">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={[
                        ...forecastData.historical.months.map((m, i) => ({
                          month: m,
                          actual: forecastData.historical.values[i],
                          p25: null, p50: null, p75: null
                        })),
                        ...forecastData.forecast.months.map((m, i) => ({
                          month: m,
                          actual: null,
                          p25: forecastData.forecast.p25[i],
                          p50: forecastData.forecast.p50[i],
                          p75: forecastData.forecast.p75[i]
                        }))
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                        <XAxis dataKey="month" tick={{ fill: '#A1A1AA', fontSize: 9 }} interval={2} />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 9 }} tickFormatter={formatNumber} />
                        <RechartsTooltip contentStyle={{ backgroundColor: '#0F0F10', border: '1px solid rgba(255,255,255,0.1)', fontSize: '10px' }} />
                        <Area type="monotone" dataKey="p75" stroke="transparent" fill="#10B981" fillOpacity={0.1} />
                        <Area type="monotone" dataKey="p25" stroke="transparent" fill="#0F0F10" />
                        <Line type="monotone" dataKey="actual" stroke="#F59E0B" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="p50" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                    <div className="p-1.5 rounded bg-white/[0.02]">
                      <p className="text-muted-foreground">Historical Slope</p>
                      <p className="font-mono text-foreground">{formatNumber(forecastData.historical.slope)}</p>
                    </div>
                    <div className="p-1.5 rounded bg-white/[0.02]">
                      <p className="text-muted-foreground">Forecast Slope</p>
                      <p className="font-mono text-foreground">{formatNumber(forecastData.forecast.slope)}</p>
                    </div>
                    <div className="p-1.5 rounded bg-white/[0.02]">
                      <p className="text-muted-foreground">Variance</p>
                      <p className="font-mono text-foreground">{formatNumber(forecastData.confidence_metrics.variance)}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 4: Momentum Methodology (if operator selected) */}
        {momentumData && (
          <Card className="bg-surface border-white/10">
            <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5 text-secondary" />
                  Momentum Score: {momentumData.operator.name}
                </CardTitle>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-[10px] h-6">
                      <HelpCircle className="w-3 h-3 mr-1" /> Methodology
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-surface border-white/10 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-sm">Momentum Score Methodology</DialogTitle>
                      <DialogDescription className="text-xs">
                        <div className="space-y-3 mt-4">
                          <p>The momentum score is a weighted composite of four components:</p>
                          <ul className="space-y-2">
                            <li><strong>3-Month Growth (30%)</strong>: Recent production growth rate</li>
                            <li><strong>6-Month Acceleration (25%)</strong>: Change in growth momentum</li>
                            <li><strong>Decline Slope (25%)</strong>: Improvement in decline trajectory</li>
                            <li><strong>Volatility (20%)</strong>: Production stability (penalizes instability)</li>
                          </ul>
                          <div className="pt-2 border-t border-white/10">
                            <p className="font-medium">Score Interpretation:</p>
                            <ul className="text-muted-foreground">
                              <li>80+: Excellent</li>
                              <li>60-79: Good</li>
                              <li>40-59: Average</li>
                              <li>20-39: Poor</li>
                              <li>&lt;20: Critical</li>
                            </ul>
                          </div>
                        </div>
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-2.5 sm:p-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                {Object.entries(momentumData.components).map(([key, comp]) => (
                  <div key={key} className="p-3 rounded bg-white/[0.02] border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] text-muted-foreground uppercase">{key.replace('_', ' ')}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{(comp.weight * 100).toFixed(0)}%</span>
                    </div>
                    <p className="text-lg font-bold text-foreground">{comp.score.toFixed(0)}</p>
                    <Progress value={comp.score} className="h-1 mt-1" />
                    <p className="text-[10px] text-muted-foreground mt-1">{comp.description}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded bg-primary/10 border border-primary/20 flex items-center justify-between">
                <span className="text-sm font-medium">Composite Score</span>
                <span className="text-2xl font-bold text-primary">{momentumData.momentum_score.toFixed(1)}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
};

export default Analytics;
